import React, { useState } from 'react'
import {
  useGetAssetHolderDisbursementAccountsQuery, useGetAssetHolderDisbursementInstructionsQuery
} from '../../../../api-services/payments/paymentsApi'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { Box, Tooltip, Zoom, Button, Switch } from '@mui/material'
import { DataGrid, type GridRenderCellParams, type GridColDef } from '@mui/x-data-grid'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import CurrencyCodeChip from '../../../components/chips/CurrencyCodeChip'
import { DisbursementAccountType } from '../../../../api-schema-types/payments/DisbursementInstructionDomainDtos'
import DisbursementInstructionStateChip from '../../finance-area/disbursements/DisbursementInstructionStateChip'
import { type EditAssetHolderProps } from '../EditAssetHolderForm'
import CancelDisbursementInstructionComponent
  from '../../../components/disbursements/CancelDisbursementInstructionComponent'

const AssetHolderDisbursementsPage: React.FC<EditAssetHolderProps> = ({ assetHolder }) => {
  const getDisbursementInstructions = useGetAssetHolderDisbursementInstructionsQuery(assetHolder.assetHolderId)
  const getDisbursementAccounts = useGetAssetHolderDisbursementAccountsQuery(assetHolder.assetHolderId)

  const [instructionToCancel, setInstructionToCancel] = useState<number | null>(null)

  const pendingInstructionsColumns: GridColDef[] = [
    { field: 'paymentInstructionId', headerName: 'ID', width: 5, editable: false },
    {
      field: 'currencyCode',
      headerName: 'Currency',
      flex: 5,
      editable: false,
      renderCell: params => <CurrencyCodeChip code={params.row.currencyCode} name={`[${params.row.currencyCode}] ${params.row.currencyCode}`}/>
    },
    { field: 'schedule', headerName: 'Schedule', flex: 8, editable: false },
    {
      field: 'state',
      headerName: 'State',
      flex: 10,
      editable: false,
      renderCell: params => <DisbursementInstructionStateChip batchState={params.row.state}/>
    },

    { field: 'requestAmountFormatted', headerName: 'Requested', flex: 8, editable: false, align: 'right', headerAlign: 'center' },
    { field: 'feeAmount', headerName: 'Fee', flex: 8, editable: false, align: 'right', headerAlign: 'center' },
    { field: 'payoutAmount', headerName: 'Payout', flex: 8, editable: false, align: 'right', headerAlign: 'center' },
    { field: 'confirmedAtFormatted', headerName: 'Confirmed', flex: 8, editable: false },
    {
      field: 'processedAtFormatted',
      headerName: 'Processed',
      flex: 8,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={params.row.processedByEmailAddress} TransitionComponent={Zoom} arrow placement={'bottom'}>
            { params.row.processedAtFormatted }
          </Tooltip>
        )
      }
    },
    {
      field: 'cancelledAtFormatted',
      headerName: 'Cancelled',
      flex: 8,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={params.row.cancelledByEmailAddress} TransitionComponent={Zoom} arrow placement={'bottom'}>
            { params.row.cancelledAtFormatted }
          </Tooltip>
        )
      }
    },
    {
      field: 'disbursementAccountId',
      headerName: 'Disbursement Account',
      flex: 8,
      editable: false,
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={JSON.stringify({
            ...params.row.accountDetails,
            disbursementAccountId: params.row.disbursementAccountId
          }, null, 2)} TransitionComponent={Zoom} arrow placement={'bottom'}>
            <InfoIcon/>
          </Tooltip>
        )
      }
    },
    {
      field: 'actions',
      headerName: '',
      flex: 8,
      renderCell: params => {
        return (
          <div>
            {/* eslint-disable-next-line no-extra-boolean-cast */}
            <Button variant="outlined" onClick={() => { setInstructionToCancel(params.row.paymentInstructionId) }} disabled={!(Boolean(params.row.isCancellable))}>
              Cancel
            </Button>
          </div>
        )
      }
    }
  ]

  const disbursementAccounts: GridColDef[] = [
    { field: 'disbursementAccountId', headerName: 'ID', width: 35, editable: false },
    {
      field: 'currencyCode',
      headerName: 'Currency',
      flex: 10,
      editable: false
    },
    {
      field: 'active',
      headerName: 'Active',
      flex: 10,
      editable: false,
      renderCell: params => (
        <Switch checked={params.row.active} disabled={true} />
      )
    },
    { field: 'lastPaidAtFormatted', headerName: 'Last Paid', flex: 10, editable: false },
    {
      field: 'createdAtFormatted',
      headerName: 'Created',
      flex: 10,
      editable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={params.row.createdByEmailAddress} TransitionComponent={Zoom} arrow placement={'bottom'}>
            { params.row.createdAtFormatted }
          </Tooltip>
        )
      }
    },
    { field: 'accountStatusDescription', headerName: 'Account Status', flex: 20, editable: false },
    { field: 'verifiedAtFormatted', headerName: 'Verified At', flex: 20, editable: false },
    { field: 'verifiedByEmailAddress', headerName: 'Verified By', flex: 20, editable: false },

    {
      field: 'bitcoinAddress',
      headerName: 'Bitcoin Address',
      flex: 20,
      editable: false,
      renderCell: params => (
        <>
          { params.row.accountDetails.accountType === DisbursementAccountType.BITCOIN_ADDRESS ? params.row.accountDetails.bitcoinAddress : '-' }
        </>
      )
    },
    {
      field: 'bankAccount',
      headerName: 'Bank Account',
      flex: 20,
      editable: false,
      renderCell: params => (
        <>
          { params.row.accountDetails.accountType === DisbursementAccountType.BANK_ACCOUNT
            ? `${params.row.accountDetails.bankName} ${params.row.accountDetails.bankAccountNumber} | ${params.row.accountDetails.accountHolderName} | ${params.row.accountDetails.branchCode}`
            : '-' }
        </>
      )
    }
  ]

  return (
    <>
      <Box>
        <h3>Disbursement Accounts</h3>
        { getDisbursementAccounts.isLoading && <LoadingComponent />}
        { getDisbursementAccounts.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getDisbursementAccounts.error)} />}
        { getDisbursementAccounts.isSuccess && <><Box sx={{ mt: 2 }}>
          <DataGrid getRowId={(row) => row.disbursementAccountId}
                    rows={getDisbursementAccounts.data}
                    columns={disbursementAccounts}
                    autoHeight={true} pagination pageSize={5} rowsPerPageOptions={[5]}/>
        </Box></>}
      </Box>

      <Box sx={{ mt: 3 }}>
        <h3>Disbursement Instructions</h3>
        { getDisbursementInstructions.isLoading && <LoadingComponent />}
        { getDisbursementInstructions.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getDisbursementInstructions.error)} />}
        { getDisbursementInstructions.isSuccess && <><Box sx={{ mt: 2 }}>
          <DataGrid getRowId={(row) => row.paymentInstructionId}
                    rows={getDisbursementInstructions.data}
                    columns={pendingInstructionsColumns}
                    autoHeight={true}
                    pagination
                    pageSize={20}
                    rowsPerPageOptions={[20]}/>
        </Box></>}
      </Box>
      {instructionToCancel !== null &&
        <CancelDisbursementInstructionComponent
          assetHolderId={assetHolder.assetHolderId}
          instructionId={instructionToCancel}
          onCloseDialog={() => { setInstructionToCancel(null) }}
          onSuccess={async () => await getDisbursementInstructions.refetch()}
        />
      }
    </>
  )
}

export default AssetHolderDisbursementsPage
